// Editor for a prompt-built website: sections and theme on the left, the real
// page in the middle (click text to type, click a photo to swap it), the
// selected section's tools on the right. Work autosaves as a draft; Publish
// puts it on the live site.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowLeft, Check, CloudOff, Eye, Layers, Loader2, Monitor, MoreVertical, Palette, Redo2,
  Settings2, SlidersHorizontal, Smartphone, Tablet, Undo2, X,
} from "lucide-react";
import { useDiscardDraftMutation, usePublishSiteMutation } from "../../store/Reducer/templateApis";
import { ConfirmDialog } from "../admin-dashboard/ui";
import { useDraftAutosave } from "../website-editor/useDraftAutosave";
import SitePreview, { fetchPreviewHtml } from "./SitePreview";
import SectionPanel from "./SectionPanel";
import { SectionsList, SettingsPanel, ThemePanel } from "./panels";
import { AddSectionDialog, ImageDialog } from "./dialogs";
import { useSiteHistory } from "./useSiteHistory";
import { AI_SITE_KEY, htmlOf, imagesOf, LIVE_PAGES, partName, patchImage, patchText, setHtml } from "./siteModel";

const DEVICES = [
  ["desktop", "Desktop", Monitor],
  ["tablet", "Tablet", Tablet],
  ["mobile", "Mobile", Smartphone],
];

const SaveStatus = ({ status, savedAt, hasDraft }) => {
  const time = savedAt?.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const map = {
    pending: [Loader2, "Unsaved changes", "text-gray-500", false],
    saving: [Loader2, "Saving…", "text-gray-500", true],
    saved: [Check, `Draft saved${time ? ` · ${time}` : ""}`, "text-green-700", false],
    error: [CloudOff, "Couldn't save. Retrying when you edit", "text-red-600", false],
    idle: hasDraft ? [Check, "Draft saved", "text-green-700", false] : [Check, "Up to date with live site", "text-gray-500", false],
  };
  const [Icon, text, tone, spin] = map[status] || map.idle;
  return (
    <span className={`hidden items-center gap-1.5 text-xs md:inline-flex ${tone}`} role="status" aria-live="polite">
      <Icon size={14} className={spin ? "animate-spin" : ""} /> {text}
    </span>
  );
};

const IconButton = ({ label, onClick, disabled, active, children }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} aria-pressed={active}
    className={`rounded-lg p-2 transition disabled:opacity-30 ${active ? "bg-accent/10 text-accent" : "text-gray-600 hover:bg-gray-100"}`}>
    {children}
  </button>
);

const AiSiteEditor = ({ initialSite, startsAsDraft, liveUrl, onStartOver, onDiscarded }) => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.Auth?.user?.accessToken);
  const history = useSiteHistory();
  // The callbacks are stable; the object around them is not.
  const { site, reset, update, replace } = history;
  const siteRef = useRef(site);
  siteRef.current = site;

  const [version, setVersion] = useState(0); // bumped when the preview must re-render
  const [hasDraft, setHasDraft] = useState(startsAsDraft);
  const [device, setDevice] = useState(() => (window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop"));
  const [selected, setSelected] = useState(null);
  const [focusNonce, setFocusNonce] = useState(0);
  const [leftTab, setLeftTab] = useState("sections");
  const [sheet, setSheet] = useState(null); // mobile: "sections" | "theme" | "settings" | "edit"
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [image, setImage] = useState(null); // { section, id, src, alt }
  const [adding, setAdding] = useState(false);

  const [publishSite, { isLoading: publishing }] = usePublishSiteMutation();
  const [discardDraft, { isLoading: discarding }] = useDiscardDraftMutation();

  // The server returns the checked copy of each save; take it (new edit
  // markers after an HTML edit, say) unless the owner changed something since.
  const onSaved = useCallback((res, saved) => {
    const checked = res?.data?.templateMap;
    if (!checked || siteRef.current !== saved || JSON.stringify(checked) === JSON.stringify(saved)) return;
    replace(checked);
    markBaselineRef.current?.(checked);
  }, [replace]);

  const ready = Boolean(site);
  const { status, savedAt, flush, markBaseline, cancelPending } = useDraftAutosave({ templateId: AI_SITE_KEY, site, ready, onSaved });
  const markBaselineRef = useRef(markBaseline);
  markBaselineRef.current = markBaseline;

  useEffect(() => {
    reset(initialSite);
    markBaseline(initialSite);
    setVersion((v) => v + 1);
  }, [initialSite, reset, markBaseline]);

  useEffect(() => {
    if (status === "saved") setHasDraft(true);
  }, [status]);

  // Any change that alters the page (everything but typing on the page itself).
  const change = useCallback((next, group) => {
    update(next, group);
    setVersion((v) => v + 1);
  }, [update]);
  const { undo: undoStep, redo: redoStep } = history;
  const undo = useCallback(() => { undoStep(); setVersion((v) => v + 1); }, [undoStep]);
  const redo = useCallback(() => { redoStep(); setVersion((v) => v + 1); }, [redoStep]);

  // Undo / redo shortcuts, except while typing in the panels.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const el = document.activeElement;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", close);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", close); };
  }, [menuOpen]);

  // ---- Messages from the page ----
  const onText = useCallback((section, id, text) => {
    update((s) => setHtml(s, section, patchText(htmlOf(s, section), id, text)), `text-${section}-${id}`);
  }, [update]);
  // From a click on the page or the section panel's photo list.
  const onImage = useCallback((section, id) => {
    const photo = imagesOf(htmlOf(siteRef.current, section)).fixed.find((p) => p.id === id);
    setImage({ section, id, src: photo?.src || "", alt: photo?.alt || "" });
  }, []);
  const onLive = useCallback((source) => {
    const [label, path] = LIVE_PAGES[source] || [];
    if (!path) return;
    toast((t) => (
      <span className="text-sm">
        This comes from your {label}, so it is always up to date.{" "}
        <button type="button" className="font-semibold text-accent underline" onClick={() => { toast.dismiss(t.id); navigate(path); }}>Edit {label}</button>
      </span>
    ), { id: "live-data", duration: 6000 });
  }, [navigate]);
  const select = useCallback((id, fromPanel) => {
    setSelected(id);
    if (fromPanel) setFocusNonce((n) => n + 1);
    if (window.innerWidth < 1024) setSheet("edit");
  }, []);

  const pickImage = (url, alt) => {
    const { section, id } = image;
    change((s) => setHtml(s, section, patchImage(htmlOf(s, section), id, url, url ? alt : undefined)));
    setImage(null);
    toast.success(url ? "Photo changed" : "Photo removed");
  };

  const publish = async () => {
    if (!(await flush())) {
      toast.error("Couldn't save your latest changes. Check your connection and try again.");
      return;
    }
    try {
      await publishSite().unwrap();
      setHasDraft(false);
      markBaseline(siteRef.current);
      toast.success((t) => (
        <span>
          Your website is live.{" "}
          {liveUrl && <a href={liveUrl} target="_blank" rel="noreferrer" className="font-semibold underline" onClick={() => toast.dismiss(t.id)}>View it</a>}
        </span>
      ), { duration: 6000 });
    } catch (err) {
      toast.error(err?.data?.message || "Couldn't publish. Try again.");
    }
  };

  const previewInTab = async () => {
    const win = window.open("", "_blank");
    try {
      const html = await fetchPreviewHtml(token, siteRef.current, false);
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      if (win) win.location = url;
      else window.open(url, "_blank");
    } catch (err) {
      win?.close();
      toast.error(err.message || "Couldn't open the preview.");
    }
  };

  const discard = async () => {
    try {
      await cancelPending();
      await discardDraft().unwrap();
      setConfirmDiscard(false);
      setHasDraft(false);
      toast.success("Changes discarded");
      onDiscarded();
    } catch (err) {
      toast.error(err?.data?.message || "Couldn't discard the changes.");
    }
  };

  if (!site) return null;

  const canPublish = hasDraft || ["pending", "saving", "saved"].includes(status);
  const selectedLabel = selected ? partName(site, selected) : "Edit";

  const panel = (tab) =>
    tab === "theme" ? <ThemePanel site={site} onChange={change} />
    : tab === "settings" ? <SettingsPanel site={site} onChange={change} />
    : <SectionsList site={site} selected={selected} onSelect={(id) => select(id, true)} onChange={change} onAdd={() => setAdding(true)} />;

  const sectionPanel = (onClose) => (
    <SectionPanel key={selected || "none"} site={site} id={selected} onChange={change} onClose={onClose}
      onReplaceImage={(id) => onImage(selected, id)} onLive={onLive} />
  );

  return (
    <div className="flex h-screen flex-col bg-gray-100">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-2 sm:px-4">
        <Link to="/user-dashboard/masjid-web" aria-label="Back to Masjid Web" className="inline-flex items-center gap-1.5 rounded-lg p-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
          <ArrowLeft size={18} /> <span className="hidden sm:inline">Masjid Web</span>
        </Link>
        <div className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" />
        <div className="hidden items-center rounded-lg bg-gray-100 p-0.5 sm:flex">
          {DEVICES.map(([key, label, Icon]) => (
            <IconButton key={key} label={`${label} view`} active={device === key} onClick={() => setDevice(key)}><Icon size={17} /></IconButton>
          ))}
        </div>
        <IconButton label="Undo (Ctrl+Z)" disabled={!history.canUndo} onClick={undo}><Undo2 size={17} /></IconButton>
        <IconButton label="Redo (Ctrl+Shift+Z)" disabled={!history.canRedo} onClick={redo}><Redo2 size={17} /></IconButton>

        <div className="ml-auto flex items-center gap-2">
          <SaveStatus status={status} savedAt={savedAt} hasDraft={hasDraft} />
          <button type="button" onClick={previewInTab} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-gray-50">
            <Eye size={16} /> <span className="hidden sm:inline">Preview</span>
          </button>
          <button type="button" onClick={publish} disabled={!canPublish || publishing}
            className="rounded-lg bg-accent px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300">
            {publishing ? "Publishing…" : "Publish"}
          </button>
          <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
            <IconButton label="More options" onClick={() => setMenuOpen((o) => !o)}><MoreVertical size={17} /></IconButton>
            {menuOpen && (
              <div role="menu" className="absolute right-0 z-50 mt-1 w-60 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {liveUrl && <a href={liveUrl} target="_blank" rel="noreferrer" role="menuitem" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm text-slate-700 hover:bg-gray-50">View live website</a>}
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onStartOver(); }} className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-gray-50">
                  Start over with a new prompt
                </button>
                <button type="button" role="menuitem" disabled={!hasDraft} onClick={() => { setMenuOpen(false); setConfirmDiscard(true); }}
                  className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:text-gray-300 disabled:hover:bg-transparent">
                  Discard unpublished changes
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-r border-gray-200 bg-white lg:block">
          <div role="tablist" className="flex border-b border-gray-200 px-2">
            {[["sections", "Sections", Layers], ["theme", "Theme", Palette], ["settings", "Settings", Settings2]].map(([key, label, Icon]) => (
              <button key={key} type="button" role="tab" aria-selected={leftTab === key} onClick={() => setLeftTab(key)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-2.5 py-2.5 text-sm font-medium ${leftTab === key ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-slate-800"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
          <div className="p-3">{panel(leftTab)}</div>
        </aside>

        <main className="min-w-0 flex-1 pb-14 lg:pb-0">
          <SitePreview site={site} version={version} device={device} selected={selected} focusNonce={focusNonce}
            onText={onText} onImage={onImage} onLive={onLive} onSelect={(id) => select(id, false)} />
        </main>

        <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-gray-200 bg-white lg:block">
          {sectionPanel(() => setSelected(null))}
        </aside>
      </div>

      {/* Mobile: bottom bar and sheets */}
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-gray-200 bg-white lg:hidden">
        {[["sections", "Sections", Layers], ["theme", "Theme", Palette], ["settings", "Settings", Settings2], ["edit", selectedLabel, SlidersHorizontal]].map(([key, label, Icon]) => (
          <button key={key} type="button" onClick={() => setSheet(sheet === key ? null : key)}
            className={`flex flex-col items-center gap-0.5 py-2 text-xs font-medium ${sheet === key ? "text-accent" : "text-gray-600"}`}>
            <Icon size={18} /> <span className="max-w-full truncate px-1">{label}</span>
          </button>
        ))}
      </nav>
      {sheet && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={sheet === "edit" ? "Edit section" : sheet}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setSheet(null)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex justify-end bg-white p-2">
              <button type="button" aria-label="Close" onClick={() => setSheet(null)} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            </div>
            {sheet === "edit" ? sectionPanel(() => setSheet(null)) : <div className="px-4 pb-6">{panel(sheet)}</div>}
          </div>
        </div>
      )}

      {image && <ImageDialog current={image.src} currentAlt={image.alt} onPick={pickImage} onClose={() => setImage(null)} />}
      {adding && (
        <AddSectionDialog site={site} after={selected && selected !== "header" && selected !== "footer" ? selected : null}
          onAdd={(update, id) => { change(update); setAdding(false); setSelected(id); setFocusNonce((n) => n + 1); }}
          onClose={() => setAdding(false)} />
      )}
      <ConfirmDialog
        isOpen={confirmDiscard}
        title="Discard unpublished changes?"
        description="Your website goes back to what is live now. This can't be undone."
        confirmLabel="Discard changes"
        danger
        onConfirm={discard}
        onCancel={() => setConfirmDiscard(false)}
        isLoading={discarding}
      />
    </div>
  );
};

export default AiSiteEditor;
